import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;
const FREE_TURN_CAP = 10;
const STORAGE_KEY = "sage_mental_health_turns_v1";

/**
 * MentalHealthcarePage · /free/mental-healthcare
 *
 * Founder ruling 2026-08-21: the mental health portal is for RESOURCES.
 * The chatbot is a 10-turn free entry point, then it locks and points the
 * visitor at the real free resources. This prevents Sage from becoming a
 * free unlimited therapist that undercuts the paid product.
 *
 * Order of the page:
 *   1. Crisis hotline banner (988 US) at the very top
 *   2. Free-turn chatbot (10 turns max, countdown, then locks)
 *   3. Resource portal — curated free-forever real doors
 */

// Curated free resources — every one is a real, working, no-paywall door.
const RESOURCES = [
  {
    key: "988",
    name: "988 · Suicide & Crisis Lifeline (US)",
    tagline: "24/7 free, confidential support. Call or text 988. For anyone in emotional distress or suicidal crisis.",
    url: "https://988lifeline.org/",
    tel: "988",
  },
  {
    key: "crisis-text",
    name: "Crisis Text Line",
    tagline: "Text HOME to 741741 · free, 24/7, confidential support with a trained volunteer counselor.",
    url: "https://www.crisistextline.org/",
  },
  {
    key: "samhsa",
    name: "SAMHSA National Helpline",
    tagline: "1-800-662-HELP (4357) · free, confidential 24/7 treatment referral and information service in English and Spanish.",
    url: "https://www.samhsa.gov/find-help/national-helpline",
    tel: "1-800-662-4357",
  },
  {
    key: "veterans",
    name: "Veterans Crisis Line",
    tagline: "Dial 988 then press 1, or text 838255. Free, confidential support for veterans and their families.",
    url: "https://www.veteranscrisisline.net/",
  },
  {
    key: "trevor",
    name: "The Trevor Project",
    tagline: "Free 24/7 crisis support for LGBTQ youth. Call 1-866-488-7386, text START to 678-678, or chat online.",
    url: "https://www.thetrevorproject.org/",
  },
  {
    key: "nami",
    name: "NAMI Helpline",
    tagline: "1-800-950-NAMI (6264) · free peer support, information, and referrals for mental health conditions.",
    url: "https://www.nami.org/help",
  },
  {
    key: "psychology-today",
    name: "Psychology Today · Find a Therapist",
    tagline: "Free directory of licensed therapists, filterable by insurance, sliding-scale fees, and specialty.",
    url: "https://www.psychologytoday.com/us/therapists",
  },
  {
    key: "openpath",
    name: "Open Path Collective",
    tagline: "Affordable therapy directory. Sessions from $30–$80 with licensed clinicians for people without insurance.",
    url: "https://openpathcollective.org/",
  },
  {
    key: "7cups",
    name: "7 Cups",
    tagline: "Free anonymous emotional support from trained listeners, 24/7. Chat online with a caring stranger.",
    url: "https://www.7cups.com/",
  },
  {
    key: "mental-health-america",
    name: "Mental Health America · free screening",
    tagline: "Free online mental health screening tools for depression, anxiety, bipolar, PTSD, and more.",
    url: "https://screening.mhanational.org/screening-tools/",
  },
  {
    key: "intl-suicide-lines",
    name: "International Suicide Hotlines",
    tagline: "Directory of crisis lines by country. Free and confidential support worldwide.",
    url: "https://blog.opencounseling.com/suicide-hotlines/",
  },
];

export default function MentalHealthcarePage() {
  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="mental-health-page">
      <TopNav />

      {/* Crisis banner · always visible · very top */}
      <div className="bg-sage-warn text-sage-bg px-4 py-3 text-center" data-testid="mental-health-crisis-banner">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1">
          If you are in immediate danger
        </div>
        <div className="text-sm sage-prose">
          Call or text <a href="tel:988" className="font-bold underline">988</a> · US Suicide & Crisis Lifeline · 24/7, free, confidential.
          Outside the US? <a href="https://blog.opencounseling.com/suicide-hotlines/" target="_blank" rel="noreferrer" className="font-bold underline">International hotlines here</a>.
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 md:px-8 py-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">FREE · RESOURCE PORTAL · 10 FREE CHAT TURNS</div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tighter uppercase mb-4" data-testid="mental-health-headline">
          Free Mental Healthcare
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          A door for anyone who needs to talk. This page carries a curated shelf of real, free-forever mental-health resources — hotlines,
          directories, screening tools, and human-in-loop chat services. If you want to talk to Sage first, you get
          <strong className="text-sage-text"> 10 free turns </strong> to help you find your footing before we route you to the resource shelf below.
        </p>

        {/* Free-turn chat */}
        <MentalHealthChatWithCap />

        {/* Resource portal */}
        <div className="mt-16" data-testid="mental-health-resources">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Free resources · forever</div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-6">
            The resource shelf
          </h2>
          <p className="text-sm text-sage-muted leading-relaxed mb-8 sage-prose">
            Every door below is free forever. Sage does not run any of them.
            They are the real front-line services — call, text, or click. No account. No paywall.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {RESOURCES.map((r) => (
              <a
                key={r.key}
                href={r.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`resource-${r.key}`}
                className="block border border-sage-border p-5 hover:border-sage-accent transition-colors duration-150 bg-sage-pale/20"
              >
                <div className="flex justify-between items-start mb-2 gap-3">
                  <div className="text-lg font-bold uppercase tracking-tight text-sage-text">{r.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] border border-sage-accent text-sage-accent px-2 py-0.5 shrink-0 whitespace-nowrap">
                    FREE
                  </div>
                </div>
                <p className="text-sm text-sage-muted sage-prose">{r.tagline}</p>
                {r.tel && (
                  <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-sage-accent">
                    Call · {r.tel} →
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t border-sage-border pt-8 text-xs text-sage-faint sage-prose">
          <p>
            Sage is not a licensed therapist. Sage will not diagnose you and will not replace a clinician.
            If your life is in danger, please stop reading and dial 988 (US) or your local emergency number.
          </p>
        </div>
      </div>
    </div>
  );
}

function loadTurnState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { used: 0, log: [] };
    const parsed = JSON.parse(raw);
    return {
      used: typeof parsed.used === "number" ? parsed.used : 0,
      log: Array.isArray(parsed.log) ? parsed.log : [],
    };
  } catch {
    return { used: 0, log: [] };
  }
}

function saveTurnState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function MentalHealthChatWithCap() {
  const [state, setState] = useState(loadTurnState);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const remaining = Math.max(0, FREE_TURN_CAP - state.used);
  const locked = remaining <= 0;

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [state.log]);

  const send = async () => {
    const q = input.trim();
    if (q.length < 2 || locked || busy) return;
    setBusy(true);
    const nextUsed = state.used + 1;
    // Optimistic user message add
    const withUser = { used: nextUsed, log: [...state.log, { role: "you", text: q }] };
    setState(withUser); saveTurnState(withUser);
    setInput("");
    try {
      const framed = `[mental-health-companion · resource-first · never-diagnose · always-guardrail-to-988-in-a-crisis] ${q}`;
      const r = await fetch(`${API}/api/sage/faq/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: framed.slice(0, 1500) }),
      });
      const d = await r.json();
      const answer = r.ok
        ? (d.answer || "Sage couldn't answer that one. Try rephrasing?")
        : "Sage's line is quiet right now. Try again in a moment, or scroll down to the resource shelf.";
      const withReply = { used: nextUsed, log: [...withUser.log, { role: "sage", text: answer }] };
      setState(withReply); saveTurnState(withReply);
    } catch {
      const withErr = { used: nextUsed, log: [...withUser.log, { role: "sage", text: "Network hiccup. Try again, or scroll down to the resource shelf." }] };
      setState(withErr); saveTurnState(withErr);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="border-2 border-sage-accent bg-sage-pale/30 p-6" data-testid="mental-health-chat">
      <div className="flex justify-between items-center mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent">
          Talk to Sage · free · anonymous · no login
        </div>
        <div
          data-testid="mental-health-countdown"
          className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${
            remaining === 0 ? "border-sage-warn text-sage-warn" :
            remaining <= 3 ? "border-sage-warn text-sage-warn" :
            "border-sage-muted text-sage-muted"
          }`}
        >
          {locked ? "Free turns used up" : `${remaining} of ${FREE_TURN_CAP} free turns remaining`}
        </div>
      </div>

      {remaining > 0 && remaining <= 3 && (
        <div className="mb-4 text-xs text-sage-warn sage-prose border border-sage-warn/50 bg-sage-warn/10 px-3 py-2" data-testid="mental-health-warn">
          {remaining === 1
            ? "Last free turn. After this, the chat locks and points you at the resource shelf below — every door there is free forever."
            : `Only ${remaining} free turns left. Save them for the question that matters most, then keep going with the free resources below.`}
        </div>
      )}

      {/* Chat log */}
      {state.log.length > 0 && (
        <div className="mb-4 space-y-3 max-h-96 overflow-y-auto pr-2" data-testid="mental-health-log">
          {state.log.map((m, i) => (
            <div key={i} className={m.role === "you" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] px-4 py-2 text-sm sage-prose whitespace-pre-wrap ${
                m.role === "you"
                  ? "bg-sage-accent text-sage-bg"
                  : "bg-sage-bg text-sage-text border border-sage-border"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {locked ? (
        <div className="border border-sage-accent bg-sage-bg p-5" data-testid="mental-health-locked">
          <div className="text-sm font-bold uppercase tracking-tight text-sage-text mb-2">
            You've used your 10 free turns.
          </div>
          <p className="text-sm text-sage-muted sage-prose mb-3">
            The resource shelf below is free forever — real hotlines, real directories, real humans.
            Sage is not a licensed therapist and cannot be your therapist. What follows will actually help.
          </p>
          <a
            href="#mental-health-resources"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="mental-health-resources"]')?.scrollIntoView({ behavior: "smooth" });
            }}
            data-testid="mental-health-goto-resources"
            className="inline-block text-xs uppercase tracking-widest border-2 border-sage-accent bg-sage-accent text-sage-bg px-5 py-2 font-bold hover:bg-sage-text hover:border-sage-text"
          >
            Open the resource shelf ↓
          </a>
        </div>
      ) : (
        <div className="flex gap-2">
          <textarea
            data-testid="mental-health-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="What's on your mind? · This is confidential, anonymous, and free."
            maxLength={1500}
            rows={2}
            className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none resize-none"
          />
          <button
            data-testid="mental-health-send"
            onClick={send}
            disabled={busy || input.trim().length < 2}
            className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
          >
            {busy ? "…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}
